import { IPublicClientApplication, EndSessionRequest } from "@azure/msal-browser";
import axios, { AxiosHeaders } from "axios";
import { getStoredLocalAuthUserId, isLocalAuthEnabled, localAuthHeaderName } from "./localAuthConfig";

declare global {
  var __sgAuthInterceptorId: number | undefined;
}
let apiAuthClient: IPublicClientApplication | undefined;
let apiAuthScopes: string[] = [];
export const getConfiguredAuthHeaders = () => getAuthHeaders(apiAuthClient, apiAuthScopes);

export async function acquireAccessToken(
  instance: IPublicClientApplication | undefined,
  scopes: string[]
): Promise<string> {
  if (isLocalAuthEnabled) {
    return "";
  }

  if (!instance) {
    throw Error("MSAL instance is not available.");
  }

  const activeAccount = instance.getActiveAccount();
  const accounts = instance.getAllAccounts();

  if (!activeAccount && accounts.length === 0) {
    /*
     * User is not signed in. Throw error or wait for user to login.
     * Do not attempt to log a user in outside of the context of MsalProvider
     */
    throw Error("User is not logged in...");
  }

  const request = {
    scopes: scopes,
    account: activeAccount || accounts[0],
  };

  try {
    const authResult = await instance.acquireTokenSilent(request);
    return authResult.accessToken;
  } catch (error) {
    console.warn(error);
    instance.logoutRedirect({
      postLogoutRedirectUri: "/",
      mainWindowRedirectUri: "/",
    } as EndSessionRequest);
    return "";
  }
}

export function registerAxiosAuthorizationHeaderInterceptor(
  msalInstance?: IPublicClientApplication,
  scopes: string[] = []
) {
  apiAuthClient = msalInstance;
  apiAuthScopes = scopes;
  if (globalThis.__sgAuthInterceptorId !== undefined) {
    return;
  }

  globalThis.__sgAuthInterceptorId = axios.interceptors.request.use(async (config) => {
    const headers = AxiosHeaders.from(config.headers);
    const localUserId = getStoredLocalAuthUserId();

    if (localUserId) {
      headers.set(localAuthHeaderName, localUserId);
    }

    if (!isLocalAuthEnabled) {
      const token = await acquireAccessToken(msalInstance, scopes);
      headers.set("Authorization", `Bearer ${token}`);
    }

    config.headers = headers;
    return config;
  });

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      if (globalThis.__sgAuthInterceptorId !== undefined) {
        axios.interceptors.request.eject(globalThis.__sgAuthInterceptorId);
        globalThis.__sgAuthInterceptorId = undefined;
      }
    });
  }
}

export async function getAuthHeaders(
  msalInstance?: IPublicClientApplication,
  scopes: string[] = []
): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};
  const localUserId = getStoredLocalAuthUserId();

  if (localUserId) {
    headers[localAuthHeaderName] = localUserId;
  }

  if (isLocalAuthEnabled) {
    return headers;
  }

  const token = await acquireAccessToken(msalInstance, scopes);
  headers.Authorization = `Bearer ${token}`;
  return headers;
}
